import React from 'react';
import {
  ImageBackground,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import {TFirebaseTalkingPoints} from '@chancer/common/lib/interfaces/firestore/FirestoreClientInterfaces';
import {
  TStructuredTalkingPoint,
  TTalkingPointArticleSummary,
} from '@chancer/common/lib/interfaces/firestore/PredictionInterfaces';
import {getFormattedNewsDate} from '@chancer/common/lib/utils/DateUtils';
import {
  COLOR_GREY_6,
  COLOR_LIGHT,
  COLOR_LOADING_GREY,
  COLOR_YELLOW,
  FONT_COPY_MEDIUM,
  FONT_SIZE_LABEL,
  FONT_SIZE_META,
  FONT_SIZE_NAV,
  FONT_SUB_TITLE,
} from '@chancer/components/lib/Styles/DesignSystem-chancer';

interface IProps {
  talkingPoints: TFirebaseTalkingPoints | null;
  onUrlPress: (url: string | undefined) => void;
}

export const TalkingPointsList: React.FC<IProps> = ({
  talkingPoints,
  onUrlPress,
}) => {
  if (!talkingPoints) {
    return (
      <View style={styles.container}>
        <LoadingSkeleton />
        <LoadingSkeleton />
        <LoadingSkeleton />
      </View>
    );
  }

  const points = talkingPoints.structured
    .filter((tp) => !tp.insufficient_data)
    .slice(0, 5);

  if (points.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>No talking points available</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {points.map((tp, index) => (
        <TalkingPointCard
          key={`tp-${tp.rank}`}
          talkingPoint={tp}
          index={index}
          articles={talkingPoints.articles}
          onUrlPress={onUrlPress}
        />
      ))}
    </View>
  );
};

const RelevanceBars: React.FC<{level: number}> = ({level}) => (
  <View style={barStyles.container}>
    <View
      style={[
        barStyles.bar,
        {height: 5},
        level >= 1 ? barStyles.active : barStyles.dim,
      ]}
    />
    <View
      style={[
        barStyles.bar,
        {height: 8},
        level >= 2 ? barStyles.active : barStyles.dim,
      ]}
    />
    <View
      style={[
        barStyles.bar,
        {height: 11},
        level >= 3 ? barStyles.active : barStyles.dim,
      ]}
    />
  </View>
);

const getRelevanceLevel = (
  articleId: string,
  tp: TStructuredTalkingPoint,
): number => {
  if (articleId === tp.bestArticleId) {
    return 3;
  }
  const idx = tp.supportingArticleIds.indexOf(articleId);
  if (idx === 0) {
    return 3;
  }
  if (idx === 1) {
    return 2;
  }
  return 1;
};

interface ITalkingPointCardProps {
  talkingPoint: TStructuredTalkingPoint;
  index: number;
  articles?: Record<string, TTalkingPointArticleSummary>;
  onUrlPress: (url: string | undefined) => void;
}

const TalkingPointCard: React.FC<ITalkingPointCardProps> = ({
  talkingPoint,
  index,
  articles,
  onUrlPress,
}) => {
  const allArticleIds = [
    talkingPoint.bestArticleId,
    ...talkingPoint.supportingArticleIds.filter(
      (id) => id !== talkingPoint.bestArticleId,
    ),
  ];

  const resolvedArticles = articles
    ? allArticleIds
        .map((id) => ({id, article: articles[id]}))
        .filter(
          (a): a is {id: string; article: TTalkingPointArticleSummary} =>
            !!a.article,
        )
        .slice(0, 3)
    : [];

  return (
    <View style={cardStyles.container}>
      <View style={cardStyles.header}>
        <View style={cardStyles.rankBadge}>
          <Text style={cardStyles.rankText}>{index + 1}</Text>
        </View>
        <Text style={cardStyles.title}>{talkingPoint.title}</Text>
      </View>
      <Text style={cardStyles.summary} numberOfLines={5} ellipsizeMode="tail">{talkingPoint.summary}</Text>
      {resolvedArticles.length > 0 && (
        <View style={cardStyles.articlesContainer}>
          <View style={cardStyles.divider} />
          {resolvedArticles.map(({id, article}) => (
            <ArticleRow
              key={id}
              article={article}
              isBestMatch={id === talkingPoint.bestArticleId}
              relevanceLevel={getRelevanceLevel(id, talkingPoint)}
              onPress={() => onUrlPress(article.url)}
            />
          ))}
        </View>
      )}
    </View>
  );
};

interface IArticleRowProps {
  article: TTalkingPointArticleSummary;
  isBestMatch: boolean;
  relevanceLevel: number;
  onPress: () => void;
}

const ArticleRow: React.FC<IArticleRowProps> = ({
  article,
  isBestMatch,
  relevanceLevel,
  onPress,
}) => (
  <Pressable style={articleStyles.container} onPress={onPress}>
    {article.imageThumb ? (
      <ImageBackground
        resizeMode="cover"
        source={{uri: article.imageThumb}}
        style={articleStyles.image}
      />
    ) : (
      <View style={[articleStyles.image, articleStyles.imagePlaceholder]} />
    )}
    <View style={articleStyles.copyContainer}>
      <Text style={articleStyles.title} ellipsizeMode="tail" numberOfLines={2}>
        {article.title ?? article.domain}
      </Text>
      <View style={articleStyles.metaRow}>
        {article.icon && (
          <ImageBackground
            resizeMode="cover"
            source={{uri: article.icon}}
            style={articleStyles.publisherIcon}
          />
        )}
        <Text style={articleStyles.publisherCopy}>
          {article.siteName ?? article.domain}
          {article.publishedAt
            ? ` · ${getFormattedNewsDate(new Date(article.publishedAt))}`
            : ''}
        </Text>
        <View style={articleStyles.spacer} />
        {isBestMatch ? (
          <View style={articleStyles.bestMatchChip}>
            <Text style={articleStyles.bestMatchText}>Best match</Text>
          </View>
        ) : (
          <RelevanceBars level={relevanceLevel} />
        )}
      </View>
    </View>
  </Pressable>
);

const LoadingSkeleton: React.FC = () => (
  <View style={cardStyles.container}>
    <View style={cardStyles.header}>
      <View
        style={[
          cardStyles.rankBadge,
          {backgroundColor: COLOR_LOADING_GREY},
        ]}
      />
      <View
        style={{
          height: 16,
          width: '70%',
          backgroundColor: COLOR_LOADING_GREY,
          borderRadius: 4,
        }}
      />
    </View>
    <View
      style={{
        height: 14,
        width: '100%',
        backgroundColor: COLOR_LOADING_GREY,
        borderRadius: 4,
        marginBottom: 6,
      }}
    />
    <View
      style={{
        height: 14,
        width: '85%',
        backgroundColor: COLOR_LOADING_GREY,
        borderRadius: 4,
      }}
    />
  </View>
);

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  emptyContainer: {
    paddingHorizontal: 16,
    paddingTop: 48,
    alignItems: 'center',
  },
  emptyText: {
    color: COLOR_GREY_6,
    fontSize: FONT_SIZE_META,
    fontFamily: FONT_COPY_MEDIUM,
  },
});

const cardStyles = StyleSheet.create({
  container: {
    marginBottom: 24,
    paddingBottom: 4,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  rankBadge: {
    width: 28,
    height: 28,
    borderRadius: 4,
    backgroundColor: COLOR_YELLOW,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  rankText: {
    color: '#000000',
    fontSize: FONT_SIZE_NAV,
    fontFamily: FONT_SUB_TITLE,
  },
  title: {
    color: COLOR_LIGHT,
    fontSize: 13,
    fontFamily: FONT_SUB_TITLE,
    flex: 1,
  },
  summary: {
    color: COLOR_GREY_6,
    fontSize: 13,
    fontFamily: FONT_COPY_MEDIUM,
    lineHeight: 18,
  },
  articlesContainer: {
    marginTop: 12,
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    marginBottom: 12,
  },
});

const articleStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  image: {
    height: 64,
    width: 64,
    borderRadius: 6,
    overflow: 'hidden',
    marginRight: 12,
  },
  imagePlaceholder: {
    backgroundColor: COLOR_LOADING_GREY,
  },
  copyContainer: {
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
  },
  title: {
    color: COLOR_LIGHT,
    fontSize: FONT_SIZE_META,
    fontFamily: FONT_COPY_MEDIUM,
    marginBottom: 6,
    lineHeight: 16,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  publisherIcon: {
    height: 16,
    width: 16,
    borderRadius: 3,
    overflow: 'hidden',
    marginRight: 6,
  },
  publisherCopy: {
    color: COLOR_GREY_6,
    fontSize: FONT_SIZE_LABEL,
    fontFamily: FONT_COPY_MEDIUM,
  },
  spacer: {
    flex: 1,
  },
  bestMatchChip: {
    backgroundColor: 'rgba(246, 183, 0, 0.2)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  bestMatchText: {
    color: COLOR_YELLOW,
    fontSize: FONT_SIZE_LABEL,
    fontFamily: FONT_COPY_MEDIUM,
  },
});

const barStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 2,
  },
  bar: {
    width: 3,
    borderRadius: 1,
  },
  active: {
    backgroundColor: COLOR_YELLOW,
  },
  dim: {
    backgroundColor: 'rgba(246, 183, 0, 0.25)',
  },
});
